// A small modal. Port of the .msc-modal markup the PHP app used for its create
// and edit dialogs (see docs/migration-gaps.md item 3).
//
// Appended to <body>, NOT to the view's outlet — the same constraint lightbox.ts
// documents. The router calls `outlet.replaceChildren()` on navigation, so an
// overlay living inside the outlet is torn out mid-interaction and leaves the
// scroll lock behind.
//
// Not a native <dialog>: the SPA supports a masquerade banner and a sticky
// header, and ::backdrop plus the top layer interact badly with both. A plain
// overlay is what the rest of this codebase already does.

import { el } from '../dom.js';

export interface ModalOptions {
  title: string;
  /** The modal's content. Usually a form. */
  body: HTMLElement;
  /** Called after the modal closes, by any route (×, Cancel, backdrop, Escape). */
  onClose?: () => void;
}

export interface ModalHandle {
  open(): void;
  close(): void;
  /** Remove it from the document entirely. */
  destroy(): void;
  /** The dialog element, for appending footer buttons. */
  dialog: HTMLElement;
}

export function modal(opts: ModalOptions): ModalHandle {
  const closeBtn = el('button', { type: 'button', class: 'btn-link msc-modal-close', 'aria-label': 'Close' }, '×');
  const heading = el('h2', { class: 'msc-modal-title', id: 'msc-modal-title' }, opts.title);

  const dialog = el('div', { class: 'msc-modal-dialog card' }, [
    el('div', { class: 'msc-modal-head' }, [heading, closeBtn]),
    opts.body,
  ]);

  const backdrop = el('div', { class: 'msc-modal-backdrop' });
  const overlay = el(
    'div',
    { class: 'msc-modal', hidden: true, role: 'dialog', 'aria-modal': 'true', 'aria-labelledby': 'msc-modal-title' },
    [backdrop, dialog],
  );

  let lastFocused: HTMLElement | null = null;

  function open(): void {
    // Remember what to hand focus back to, so closing does not dump the user at
    // the top of the page.
    lastFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    overlay.hidden = false;
    document.body.classList.add('modal-open');
    // First focusable field, so the modal is usable from the keyboard the moment
    // it opens. Matches the PHP version's autofocus on the name input.
    const first = dialog.querySelector<HTMLElement>('input:not([type=hidden]), select, textarea, button');
    first?.focus();
  }

  function close(): void {
    overlay.hidden = true;
    document.body.classList.remove('modal-open');
    lastFocused?.focus();
    opts.onClose?.();
  }

  closeBtn.addEventListener('click', close);
  // The backdrop closes; the dialog must not, or every click inside dismisses it.
  backdrop.addEventListener('click', close);

  // One document-level handler for the lifetime of this modal, bound here rather
  // than on open so repeated opens do not stack listeners (lightbox.ts learned
  // the same lesson).
  document.addEventListener('keydown', (ev) => {
    if (overlay.hidden) return;
    if (ev.key === 'Escape') close();
  });

  document.body.append(overlay);

  return {
    open,
    close,
    destroy: () => overlay.remove(),
    dialog,
  };
}
